export type TrainingCorrectionFileEntry = {
  question: string;
  answer: string;
  sortOrder: number | null;
};

export type TrainingCorrectionsFile = {
  count: number;
  corrections: TrainingCorrectionFileEntry[];
};

export const TRAINING_CORRECTIONS_FILE_PARSE_ERROR =
  'INVALID_TRAINING_CORRECTIONS_FILE';

function isIntegerOrNull(value: unknown): value is number | null {
  return (
    value === null || (typeof value === 'number' && Number.isInteger(value))
  );
}

function parseCorrectionEntry(value: unknown): TrainingCorrectionFileEntry {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(TRAINING_CORRECTIONS_FILE_PARSE_ERROR);
  }

  const record = value as Record<string, unknown>;
  if (
    typeof record.question !== 'string' ||
    typeof record.answer !== 'string' ||
    !isIntegerOrNull(record.sortOrder)
  ) {
    throw new Error(TRAINING_CORRECTIONS_FILE_PARSE_ERROR);
  }

  const question = record.question.trim();
  const answer = record.answer.trim();
  if (question.length === 0 || answer.length === 0) {
    throw new Error(TRAINING_CORRECTIONS_FILE_PARSE_ERROR);
  }

  return {
    question,
    answer,
    sortOrder: record.sortOrder,
  };
}

export function serializeTrainingCorrectionsFile(
  corrections: TrainingCorrectionFileEntry[],
): string {
  const payload: TrainingCorrectionsFile = {
    count: corrections.length,
    corrections: corrections.map((correction) => ({
      question: correction.question,
      answer: correction.answer,
      sortOrder: correction.sortOrder,
    })),
  };

  return `${JSON.stringify(payload, null, 2)}\n`;
}

export function parseTrainingCorrectionsFile(
  text: string,
): TrainingCorrectionsFile {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error(TRAINING_CORRECTIONS_FILE_PARSE_ERROR);
  }

  if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error(TRAINING_CORRECTIONS_FILE_PARSE_ERROR);
  }

  const record = parsed as Record<string, unknown>;
  if (
    typeof record.count !== 'number' ||
    !Number.isInteger(record.count) ||
    record.count < 0 ||
    !Array.isArray(record.corrections)
  ) {
    throw new Error(TRAINING_CORRECTIONS_FILE_PARSE_ERROR);
  }

  const corrections = record.corrections.map(parseCorrectionEntry);
  if (corrections.length !== record.count) {
    throw new Error(TRAINING_CORRECTIONS_FILE_PARSE_ERROR);
  }

  return {
    count: record.count,
    corrections,
  };
}

export function downloadTrainingCorrectionsFile(
  chatbotName: string,
  contents: string,
): void {
  const blob = new Blob([contents], { type: 'application/json' });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = window.document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = `training-corrections-${chatbotName}.json`;
  anchor.click();
  URL.revokeObjectURL(objectUrl);
}
